// src/api/user-invites.js
import dynamicAPI from './dynamicAPI';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useMessage } from '../context/AdminMessageContext';

const base = '/api/auth/invites';

export default function useInvitesHook({ page = 1, limit = 25 } = {}) {
    const queryClient = useQueryClient();
    const { setSuccessMessage, setErrorMessage } = useMessage();

    // LIST (admin)
    const listInvites = useQuery(
        ['invites', page, limit],
        async () => {
            const res = await dynamicAPI('get', `${base}?page=${page}&limit=${limit}`);
            if (res?.error) throw new Error(res.error);
            return res;
        },
        { retry: 0, enabled: !!page }
    );

    // CREATE (admin)
    const createInvite = useMutation(
        async ({ email, hours }) => await dynamicAPI('post', base, { email, hours }),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['invites']);
                setSuccessMessage('Invite created.');
            },
            onError: (error) => setErrorMessage(error?.error || 'Failed to create invite.'),
        }
    );

    // DELETE (admin)
    const deleteInvite = useMutation(
        async (id) => await dynamicAPI('delete', `${base}/${id}`),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['invites']);
                setSuccessMessage('Invite deleted.');
            },
            onError: (error) => setErrorMessage(error?.error || 'Failed to delete invite.'),
        }
    );

    // APPROVE/REJECT (admin)
    const approveInvite = useMutation(
        async (id) => await dynamicAPI('post', `${base}/${id}/approve`),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['invites']);
                setSuccessMessage('User approved.');
            },
            onError: (error) => setErrorMessage(error?.error || 'Approval failed.'),
        }
    );

    const rejectInvite = useMutation(
        async (id) => await dynamicAPI('post', `${base}/${id}/reject`),
        {
            retry: 0,
            onSuccess: () => {
                queryClient.invalidateQueries(['invites']);
                setSuccessMessage('Invite rejected.');
            },
            onError: (error) => setErrorMessage(error?.error || 'Rejection failed.'),
        }
    );

    // PUBLIC: validate token (must be a hook -> name starts with "use")
    const useValidateInviteToken = (token) =>
        useQuery(
            ['invite-validate', token],
            async () => {
                const res = await dynamicAPI('get', `${base}/validate/${token}`);
                if (res?.error) throw new Error(res.error);
                return res;
            },
            { retry: 0, enabled: !!token }
        );

    // PUBLIC: register with invite
    // NOTE: backend route is /api/auth/register-invite (NOT under /invites)
    const registerWithInvite = useMutation(
        async ({ token, name, email, password }) =>
            await dynamicAPI('post', `/api/auth/register-invite`, {
                token,
                name,
                email,
                password,
            }),
        {
            retry: 0,
            onSuccess: () => setSuccessMessage('Registration submitted. Awaiting approval.'),
            onError: (error) => setErrorMessage(error?.error || 'Registration failed.'),
        }
    );

    return {
        // admin
        listInvites,
        createInvite,
        deleteInvite,
        approveInvite,
        rejectInvite,
        // public
        useValidateInviteToken,
        registerWithInvite,
    };
}
